import { Capacitor } from '@capacitor/core';
import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { MapEntityDrawerHeader } from 'components/Map/MapEntityDrawerHeader';
import { StatItem, StatTable } from 'components/StatTable';
import { subDays, subHours } from 'date-fns';
import { useWeatherStationWithObservations } from 'features/weatherStations/hooks/useWeatherStationWithObservations';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getTimePass } from 'shared/dates';
import { getCurrentLocationAsShareUrl } from 'shared/utils';
import { getDirectionFromBearing } from 'components/Map/utils';
import { useContext, useMemo, useState } from 'react';
import { MapEntityDrawerContext } from 'components/Map/MapEntityDrawer';
import { useTabs } from 'hooks/useTabs';
import { WeatherStationDataTable } from 'features/weatherStations/components/WeatherStationDataTable';
import { convertCelsiusToFahrenheit } from 'features/weatherStations/utils/convertCelsiusToFahrenheit';
import { parseNumericValue } from 'features/weatherStations/utils/parseNumericValue';
import { convertMetersPerSecToMph } from 'features/weatherStations/utils/convertMetersPerSecToMph';
import {
  getLatestObservation,
  getObservationsHistoricalData,
} from './WeatherStationDetails.utils';
import { WindDirectionIcon } from './WindDirectionIcon';

const OBSERVATIONS_POLL_INTERVAL = 1000 * 60 * 5; // 5 minutes
const TAB_PANEL_ID_PREFIX = 'weather-station-tab-panel';

export const WeatherStationDetails = (): JSX.Element => {
  const { stationId } = useParams<{ stationId: string }>();
  const { t } = useTranslation();
  const observationsHistoryStart = subDays(new Date(), 7);
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const [stickyHeaderContentHeight, setStickyHeaderContentHeight] = useState(0);

  const { weatherStation, observations, isLoading, error } =
    useWeatherStationWithObservations({
      id: parseInt(stationId, 10),
      start: observationsHistoryStart,
      end: new Date(),
      pollInterval: OBSERVATIONS_POLL_INTERVAL,
    });

  const historicalData = useMemo(
    () =>
      // Uncomment to experiment with values and review the table UI behavior
      // getObservationsHistoricalData(
      //   [
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 21),
      //       airTemp: 12,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 19),
      //       airTemp: 12,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 16),
      //       airTemp: 15,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 13),
      //       airTemp: 17,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 10),
      //       airTemp: 18,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 7),
      //       airTemp: 20,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 4),
      //       airTemp: 25,
      //     }),
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 1),
      //       airTemp: 36,
      //     }),
      //   ],
      //   subHours(new Date(), 24),
      //   3,
      // ),
      getObservationsHistoricalData(observations, subHours(new Date(), 24), 3),
    [observations],
  );

  const tabData = [
    {
      tabId: 'conditions',
      label: t('weatherStations.tabs.conditions'),
      enabled: true,
      content: historicalData ? (
        <Stack sx={{ p: 1, pb: 0 }} spacing={1}>
          <Typography color="secondary" variant="body2" textAlign="right">
            {t('weatherStations.conditionsTimeRange')}
          </Typography>
          <WeatherStationDataTable data={historicalData} />
        </Stack>
      ) : null,
    },
    {
      tabId: 'notifications',
      label: t('weatherStations.tabs.notifications'),
      enabled: false,
      content: null,
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('weatherStations.tabs.ariaLabel'),
    tabData,
  });

  if (isLoading || error || !weatherStation) {
    return <LoadingAndErrors isLoading={isLoading} />;
  }

  const latestObservation = getLatestObservation(observations);

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: t('home.weatherStationTitle', { name: weatherStation.name }),
      url: getCurrentLocationAsShareUrl(latestObservation?.timestamp),
    });
  };

  const stats: StatItem[] = [
    {
      label: t('weatherStations.temp'),
      value:
        typeof latestObservation?.airTemp === 'number' ? (
          <Trans
            i18nKey="weatherStations.tempValue"
            values={{
              temp: convertCelsiusToFahrenheit(latestObservation.airTemp),
            }}
          >
            63
            <Typography component="span" variant="subtitle1">
              ˚F
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.dewPoint'),
      value:
        typeof latestObservation?.dewPointTemperature === 'number' ? (
          <Trans
            i18nKey="weatherStations.tempValue"
            values={{
              temp: convertCelsiusToFahrenheit(
                latestObservation?.dewPointTemperature,
              ),
            }}
          >
            33
            <Typography component="span" variant="subtitle1">
              ˚F
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.relHumidity'),
      value:
        typeof latestObservation?.relativeHumidity === 'number'
          ? t('weatherStations.percentageValue', {
              percent: parseNumericValue(latestObservation.relativeHumidity),
            })
          : '-',
    },
    {
      label: t('weatherStations.wind'),
      value:
        typeof latestObservation?.windSpeed === 'number' ? (
          <Trans
            i18nKey="weatherStations.speedValue"
            values={{
              speed: convertMetersPerSecToMph(latestObservation.windSpeed),
            }}
          >
            15
            <Typography component="span" variant="subtitle1">
              mph
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.gust'),
      value:
        typeof latestObservation?.windGust === 'number' ? (
          <Trans
            i18nKey="weatherStations.speedValue"
            values={{
              speed: convertMetersPerSecToMph(latestObservation.windGust),
            }}
          >
            25
            <Typography component="span" variant="subtitle1">
              mph
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.direction'),
      valueTypographyProps: { textTransform: 'capitalize' },
      value:
        typeof latestObservation?.windDirectionToward === 'number' &&
        typeof latestObservation?.windDirection === 'number' ? (
          <Typography
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <span>
              {getDirectionFromBearing(latestObservation.windDirection)}
            </span>
            <WindDirectionIcon
              degrees={latestObservation.windDirectionToward}
            />
          </Typography>
        ) : (
          '-'
        ),
    },
  ];

  const shareTooltipText = Capacitor.isNativePlatform()
    ? t('common.share')
    : t('common.copyLinkToClipboard');

  return (
    <>
      <Helmet>
        <title>
          {t('home.weatherStationTitle', { name: weatherStation.name })}
        </title>
      </Helmet>

      <Box sx={{ mb: 'env(safe-area-inset-bottom)' }}>
        <DrawerStickyHeader
          scrollContainer={scrollElementRef?.current as HTMLElement}
          onStickyContentHeightChange={setStickyHeaderContentHeight}
        >
          <DrawerStickyHeader.ContentContainer>
            <MapEntityDrawerHeader>{weatherStation.name}</MapEntityDrawerHeader>
          </DrawerStickyHeader.ContentContainer>
        </DrawerStickyHeader>

        <Stack sx={{ gap: 2, p: 2 }}>
          <StatTable stats={stats} />

          {latestObservation && (
            <Typography>
              {t('common.updated')}&nbsp;
              <b>{getTimePass(latestObservation.timestamp.toISOString())}</b>
            </Typography>
          )}

          {canShareOrCopy && (
            <Tooltip title={shareTooltipText}>
              <Button
                fullWidth
                color="inherit"
                variant="outlined"
                onClick={handleShare}
                size="large"
              >
                {t('common.share')}
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Box>
      <Stack>
        <DrawerStickyHeader
          scrollContainer={scrollElementRef?.current as HTMLElement}
          topOffset={stickyHeaderContentHeight * -1}
          stickyStyle={{ marginTop: stickyHeaderContentHeight }}
        >
          <Tabs {...tabsProps}>
            {tabProps.map((props) => (
              // maintaining tab width so they are consistent even if some are hidden
              <Tab
                key={props.key}
                {...props}
                sx={{ maxWidth: 1 / tabData.length }}
              />
            ))}
          </Tabs>
        </DrawerStickyHeader>

        {tabPanels}
      </Stack>
    </>
  );
};
